/**
 * ⚙️ تنظیمات دستیار — گروه‌بندی‌شده، با نوار ذخیرهٔ چسبان.
 *
 * قبلاً همهٔ فیلدها در یک ستون بلند بودند و پیدا کردن چیزی سخت بود. حالا پنج
 * دستهٔ روشن (اتصال · هوش مصنوعی · فیلتر · شخصیت · رفتار)، هر کدام یک صفحه، و
 * دکمهٔ ذخیره همیشه پایین صفحه با نشانهٔ «تغییر ذخیره‌نشده».
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, FormControl, Grid, InputLabel, MenuItem,
  Select, Stack, Switch, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import SaveIcon from '@mui/icons-material/Save';
import SyncIcon from '@mui/icons-material/Sync';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import PsychologyRoundedIcon from '@mui/icons-material/PsychologyRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import MailRoundedIcon from '@mui/icons-material/MailRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import userbotService, { UserbotSettings } from '../../services/userbotService';
import FailoverCard from './FailoverCard';
import { SectionCard, errMsg, fa } from './kit';

type Group = 'connect' | 'ai' | 'failover' | 'filter' | 'dm' | 'persona' | 'behavior';

const GROUPS: Array<{ k: Group; label: string; icon: React.ReactElement; hint: string }> = [
  { k: 'connect', label: 'اتصال تلگرام', icon: <LinkRoundedIcon />, hint: 'کلیدهای my.telegram.org' },
  { k: 'ai', label: 'هوش مصنوعی', icon: <SmartToyRoundedIcon />, hint: 'سرویس، مدل‌ها و بودجه' },
  { k: 'failover', label: 'پشتیبان و جابه‌جایی', icon: <SwapHorizRoundedIcon />, hint: 'اگر سرویس اصلی خطا داد' },
  { k: 'filter', label: 'فیلتر و آستانه‌ها', icon: <FilterAltRoundedIcon />, hint: 'کی ارزش دارد جواب بدهیم' },
  { k: 'dm', label: 'پیام خصوصی', icon: <MailRoundedIcon />, hint: 'جواب به دایرکت' },
  { k: 'persona', label: 'شخصیت و لحن', icon: <PsychologyRoundedIcon />, hint: 'چطور حرف بزند' },
  { k: 'behavior', label: 'رفتار و زمان‌بندی', icon: <ScheduleRoundedIcon />, hint: 'تأخیر، سقف، ساعت سکوت' },
];

const PRESETS: Array<{ label: string; color?: 'success'; base: string; gate: string; writer: string }> = [
  { label: 'Cloudflare Workers AI (رایگان، از ایران باز)', color: 'success', base: 'https://api.cloudflare.com/client/v4/accounts/ACCOUNT_ID/ai/v1', gate: '@cf/meta/llama-3.1-8b-instruct-fast', writer: '@cf/meta/llama-3.3-70b-instruct-fp8-fast' },
  { label: 'Gemini (aistudio.google.com)', base: 'https://generativelanguage.googleapis.com/v1beta/openai', gate: 'gemini-flash-lite-latest', writer: 'gemini-flash-latest' },
  { label: 'OpenRouter (openrouter.ai)', base: 'https://openrouter.ai/api/v1', gate: 'google/gemini-2.0-flash-exp:free', writer: 'deepseek/deepseek-chat-v3-0324:free' },
  { label: 'Groq (console.groq.com)', base: 'https://api.groq.com/openai/v1', gate: 'llama-3.1-8b-instant', writer: 'llama-3.3-70b-versatile' },
  { label: 'Ollama لوکال', base: 'http://127.0.0.1:11434/v1', gate: 'qwen2.5:7b-instruct', writer: 'qwen2.5:7b-instruct' },
];

/** ردیف سوییچ با توضیح — تکرار همیشگی این صفحه */
const SwitchRow: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }> = ({
  checked,
  onChange,
  label,
  hint,
}) => (
  <div className="ubc-switchrow" style={{ height: '100%' }}>
    <Switch size="small" checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ mt: -0.25 }} />
    <div>
      <b>{label}</b>
      {hint && <span>{hint}</span>}
    </div>
  </div>
);

const SettingsTab: React.FC<{ initial: UserbotSettings; onSaved: () => void }> = ({ initial, onSaved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [group, setGroup] = useState<Group>('connect');
  const [s, setS] = useState<UserbotSettings>(initial);
  const [apiHash, setApiHash] = useState('');
  const [openaiKey, setOpenaiKey] = useState('');
  const [openaiBase, setOpenaiBase] = useState(initial.openai?.baseUrl || '');
  const [triggers, setTriggers] = useState(initial.prefilter.triggers.join('\n'));
  const [roots, setRoots] = useState((initial.prefilter.roots || []).join('\n'));
  const [negatives, setNegatives] = useState(initial.prefilter.negatives.join('\n'));
  const [models, setModels] = useState<string[] | null>(null);
  const [modelsBusy, setModelsBusy] = useState(false);
  const [modelsMsg, setModelsMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [alertTesting, setAlertTesting] = useState(false);

  useEffect(() => {
    setS(initial);
    setOpenaiBase(initial.openai?.baseUrl || '');
    setTriggers(initial.prefilter.triggers.join('\n'));
    setRoots((initial.prefilter.roots || []).join('\n'));
    setNegatives(initial.prefilter.negatives.join('\n'));
  }, [initial]);

  const set = <K extends keyof UserbotSettings>(k: K, v: UserbotSettings[K]) => setS((x) => ({ ...x, [k]: v }));
  const nested = <K extends keyof UserbotSettings>(k: K, patch: Partial<UserbotSettings[K]>) =>
    setS((x) => ({ ...x, [k]: { ...(x[k] as any), ...patch } }));

  const dirty = useMemo(
    () =>
      JSON.stringify(s) !== JSON.stringify(initial) ||
      apiHash.trim() !== '' ||
      openaiKey.trim() !== '' ||
      openaiBase !== (initial.openai?.baseUrl || '') ||
      triggers !== initial.prefilter.triggers.join('\n') ||
      roots !== (initial.prefilter.roots || []).join('\n') ||
      negatives !== initial.prefilter.negatives.join('\n'),
    [s, initial, apiHash, openaiKey, openaiBase, triggers, roots, negatives]
  );

  const save = async () => {
    setSaving(true);
    try {
      const lines = (t: string) => t.split('\n').map((x) => x.trim()).filter(Boolean);
      await userbotService.updateSettings({
        ...s,
        prefilter: { ...s.prefilter, triggers: lines(triggers), roots: lines(roots), negatives: lines(negatives) },
        ...(apiHash.trim() ? { apiHash: apiHash.trim() } : {}),
        openai: { baseUrl: openaiBase.trim(), ...(openaiKey.trim() ? { apiKey: openaiKey.trim() } : {}) },
      } as any);
      setApiHash('');
      setOpenaiKey('');
      enqueueSnackbar('تنظیمات ذخیره شد', { variant: 'success' });
      onSaved();
    } catch (e) {
      enqueueSnackbar(errMsg(e), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fetchModels = async () => {
    setModelsBusy(true);
    setModelsMsg('');
    setModels(null);
    try {
      const r = await userbotService.listModels();
      if (r.ok) {
        setModels(r.models);
        if (!r.models.length) setModelsMsg('ارائه‌دهنده فهرست مدل برنگرداند');
      } else setModelsMsg(r.message || 'خطا');
    } catch (e) {
      setModelsMsg(errMsg(e));
    } finally {
      setModelsBusy(false);
    }
  };

  const isOpenAi = (s.provider || 'anthropic') === 'openai';
  const claudeModelOnOther = isOpenAi && (/^claude-/.test(s.gateModel) || /^claude-/.test(s.writerModel));

  return (
    <Box sx={{ pb: 9 }}>
      {/* دسته‌ها */}
      <div className="fac-card" style={{ marginBottom: 12 }}>
        <div className="fac-card-body" style={{ padding: '10px 12px' }}>
          <div className="fac-nav-items">
            {GROUPS.map((g) => (
              <button key={g.k} type="button" className={`fac-tab ${group === g.k ? 'active' : ''}`} style={{ minWidth: 0 }} onClick={() => setGroup(g.k)}>
                {g.icon}{g.label}
              </button>
            ))}
          </div>
          <div className="fac-current" style={{ margin: '10px 2px 0' }}>
            {GROUPS.find((g) => g.k === group)?.icon}
            <div><b>{GROUPS.find((g) => g.k === group)?.label}</b><span>{GROUPS.find((g) => g.k === group)?.hint}</span></div>
          </div>
        </div>
      </div>

      {/* ───────── اتصال تلگرام ───────── */}
      {group === 'connect' && (
        <SectionCard
          icon={<LinkRoundedIcon />}
          title="اتصال تلگرام"
          hint="api_id و api_hash را از my.telegram.org (بخش API development tools) بگیرید. یک جفت برای همهٔ حساب‌ها کافی است."
        >
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                size="small"
                type="number"
                label="api_id"
                value={s.apiId ?? ''}
                onChange={(e) => set('apiId', Number(e.target.value) || undefined)}
                fullWidth
                inputProps={{ dir: 'ltr' }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                size="small"
                type="password"
                label={s.apiHashConfigured ? 'api_hash (تنظیم شده — برای تغییر، مقدار جدید بنویسید)' : 'api_hash'}
                value={apiHash}
                onChange={(e) => setApiHash(e.target.value)}
                fullWidth
                inputProps={{ dir: 'ltr' }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <SwitchRow
                checked={s.notifyAdminsOnDraft}
                onChange={(v) => set('notifyAdminsOnDraft', v)}
                label="اعلان پیش‌نویس جدید"
                hint="به سوپرادمین‌ها"
              />
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {/* ───────── هوش مصنوعی ───────── */}
      {group === 'ai' && (
        <Stack spacing={2}>
          <SectionCard
            icon={<SmartToyRoundedIcon />}
            title="ارائه‌دهنده"
            hint="Claude با کلید env سایت، یا هر API سازگار با OpenAI (Gemini، OpenRouter، Groq، Cloudflare، Ollama)."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>ارائه‌دهنده</InputLabel>
                  <Select
                    label="ارائه‌دهنده"
                    value={s.provider || 'anthropic'}
                    onChange={(e) => {
                      const p = e.target.value as 'anthropic' | 'openai';
                      set('provider', p);
                      if (p === 'openai' && /^claude-/.test(s.gateModel)) {
                        set('gateModel', PRESETS[0].gate);
                        set('writerModel', PRESETS[0].writer);
                        if (!openaiBase) setOpenaiBase(PRESETS[0].base);
                      }
                      if (p === 'anthropic' && !/^claude-/.test(s.gateModel)) {
                        set('gateModel', 'claude-haiku-4-5');
                        set('writerModel', 'claude-sonnet-5');
                      }
                    }}
                  >
                    <MenuItem value="anthropic">Claude (Anthropic) — کلید env</MenuItem>
                    <MenuItem value="openai">سازگار با OpenAI</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <SwitchRow
                  checked={Boolean(s.aiViaBot)}
                  onChange={(v) => set('aiViaBot', v)}
                  label="درخواست‌های هوش مصنوعی از طریق سرویس ربات"
                  hint="سرویس ربات خارج از ایران و پراکسی‌دار است ⇒ سرور بک‌اند فیلترشکن نمی‌خواهد."
                />
              </Grid>

              {isOpenAi && (
                <>
                  {claudeModelOnOther && (
                    <Grid item xs={12}>
                      <Alert severity="warning">
                        مدل‌های انتخاب‌شده مال Claude هستند و روی این ارائه‌دهنده کار نمی‌کنند؛ یکی از پیش‌تنظیم‌های زیر را بزنید.
                      </Alert>
                    </Grid>
                  )}
                  {/ACCOUNT_ID/.test(openaiBase) && (
                    <Grid item xs={12}>
                      <Alert severity="warning">در Base URL هنوز ACCOUNT_ID جایگزین نشده — شناسهٔ حساب کلودفلر را بگذارید.</Alert>
                    </Grid>
                  )}
                  <Grid item xs={12} md={7}>
                    <TextField
                      size="small"
                      label="Base URL"
                      value={openaiBase}
                      onChange={(e) => setOpenaiBase(e.target.value)}
                      fullWidth
                      inputProps={{ dir: 'ltr' }}
                      helperText="بدون /chat/completions در انتها"
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <TextField
                      size="small"
                      type="password"
                      label={s.openai?.apiKeyConfigured ? 'API Key (تنظیم شده — برای تغییر، مقدار جدید)' : 'API Key'}
                      value={openaiKey}
                      onChange={(e) => setOpenaiKey(e.target.value)}
                      fullWidth
                      inputProps={{ dir: 'ltr' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      پیش‌تنظیم‌ها — با یک کلیک آدرس و مدل‌ها پر می‌شوند:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {PRESETS.map((p) => (
                        <Chip
                          key={p.label}
                          size="small"
                          clickable
                          color={p.color}
                          variant="outlined"
                          label={p.label}
                          onClick={() => {
                            setOpenaiBase(p.base);
                            set('gateModel', p.gate);
                            set('writerModel', p.writer);
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </>
              )}
            </Grid>
          </SectionCard>

          <SectionCard
            icon={<SmartToyRoundedIcon />}
            title="مدل‌ها و بودجه"
            hint="دروازه‌بان ارزان و پرتکرار است؛ نویسنده فقط برای پیام‌های تأییدشده. بودجه فقط برای Claude محاسبه می‌شود."
            actions={
              isOpenAi ? (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={modelsBusy ? <CircularProgress size={12} /> : <SyncIcon />}
                  disabled={modelsBusy}
                  onClick={fetchModels}
                >
                  دریافت فهرست مدل‌ها
                </Button>
              ) : undefined
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                {isOpenAi ? (
                  <TextField size="small" label="مدل دروازه‌بان" value={s.gateModel} onChange={(e) => set('gateModel', e.target.value)} fullWidth inputProps={{ dir: 'ltr' }} />
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>مدل دروازه‌بان</InputLabel>
                    <Select label="مدل دروازه‌بان" value={s.gateModel} onChange={(e) => set('gateModel', String(e.target.value))}>
                      <MenuItem value="claude-haiku-4-5">Claude Haiku 4.5 (پیشنهادی)</MenuItem>
                      <MenuItem value="claude-sonnet-5">Claude Sonnet 5</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                {isOpenAi ? (
                  <TextField size="small" label="مدل نویسنده" value={s.writerModel} onChange={(e) => set('writerModel', e.target.value)} fullWidth inputProps={{ dir: 'ltr' }} />
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>مدل نویسنده</InputLabel>
                    <Select label="مدل نویسنده" value={s.writerModel} onChange={(e) => set('writerModel', String(e.target.value))}>
                      <MenuItem value="claude-sonnet-5">Claude Sonnet 5 (پیشنهادی)</MenuItem>
                      <MenuItem value="claude-sonnet-4-6">Claude Sonnet 4.6</MenuItem>
                      <MenuItem value="claude-haiku-4-5">Claude Haiku 4.5 (ارزان‌تر)</MenuItem>
                      <MenuItem value="claude-opus-4-8">Claude Opus 4.8</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField size="small" type="number" label="بودجهٔ روزانه ($)" value={s.dailyBudgetUsd} onChange={(e) => set('dailyBudgetUsd', Number(e.target.value))} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>حالت گروه‌های جدید</InputLabel>
                  <Select label="حالت گروه‌های جدید" value={s.defaultMode} onChange={(e) => set('defaultMode', e.target.value as any)}>
                    <MenuItem value="auto">ارسال خودکار</MenuItem>
                    <MenuItem value="draft">پیش‌نویس با تأیید</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {modelsMsg && (
                <Grid item xs={12}>
                  <Alert severity="warning">{modelsMsg}</Alert>
                </Grid>
              )}
              {models && models.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {fa(models.length)} مدل — کلیک = نویسنده، Shift+کلیک = دروازه‌بان:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxHeight: 170, overflow: 'auto' }}>
                    {models.map((m) => (
                      <Chip
                        key={m}
                        size="small"
                        clickable
                        color={m === s.writerModel ? 'primary' : m === s.gateModel ? 'secondary' : 'default'}
                        variant={m === s.writerModel || m === s.gateModel ? 'filled' : 'outlined'}
                        label={m}
                        onClick={(e) => {
                          if ((e as React.MouseEvent).shiftKey) set('gateModel', m);
                          else set('writerModel', m);
                        }}
                        sx={{ direction: 'ltr', fontFamily: 'monospace', fontSize: 11 }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}
            </Grid>
          </SectionCard>
        </Stack>
      )}

      {/* ───────── فیلتر و آستانه‌ها ───────── */}
      {group === 'filter' && (
        <Stack spacing={2}>
          <SectionCard
            icon={<FilterAltRoundedIcon />}
            title="فیلتر محلی (لایهٔ صفر — بدون هوش مصنوعی)"
            hint="بیشتر پیام‌ها همین‌جا و رایگان رد می‌شوند. یک کلمه کافی است: ریشه با هر پیشوند و پسوندی پیدا می‌شود (خر → بخرم / می‌خرم / خرید / خریدار). هر خط یک مورد."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="ریشه‌های اضافه (اختیاری)"
                  value={roots}
                  onChange={(e) => setRoots(e.target.value)}
                  fullWidth
                  multiline
                  minRows={7}
                  maxRows={14}
                  helperText="واژگان اصلی داخل موتور است؛ این‌جا فقط ریشه‌های خاص خودتان"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="عبارت‌های ثابت (اسم محصول/برند)"
                  value={triggers}
                  onChange={(e) => setTriggers(e.target.value)}
                  fullWidth
                  multiline
                  minRows={7}
                  maxRows={14}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="کلیدواژه‌های رد فوری"
                  value={negatives}
                  onChange={(e) => setNegatives(e.target.value)}
                  fullWidth
                  multiline
                  minRows={7}
                  maxRows={14}
                />
              </Grid>

              <Grid item xs={6} md={3}>
                <TextField size="small" type="number" label="حداقل طول" value={s.prefilter.minLength} onChange={(e) => nested('prefilter', { minLength: Number(e.target.value) })} fullWidth helperText="فقط وقتی ریشه/عبارت پیدا نشد" />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" type="number" label="حداکثر طول" value={s.prefilter.maxLength} onChange={(e) => nested('prefilter', { maxLength: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ step: 0.05, min: 0, max: 1 }}
                  label="حداقل اطمینان تحلیل نیت محلی (۰ تا ۱)"
                  value={s.prefilter.minIntentScore ?? 0.35}
                  onChange={(e) => nested('prefilter', { minIntentScore: Number(e.target.value) })}
                  fullWidth
                  helperText="۰٫۳۵ یعنی یک کلمهٔ روشن کافی است"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <SwitchRow checked={s.prefilter.requireTrigger} onChange={(v) => nested('prefilter', { requireTrigger: v })} label="کلیدواژه الزامی" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SwitchRow checked={s.prefilter.ignoreLinks} onChange={(v) => nested('prefilter', { ignoreLinks: v })} label="پیام لینک‌دار رد شود" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SwitchRow checked={s.prefilter.ignoreForwarded} onChange={(v) => nested('prefilter', { ignoreForwarded: v })} label="فوروارد رد شود" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <SwitchRow checked={s.prefilter.ignoreBots} onChange={(v) => nested('prefilter', { ignoreBots: v })} label="پیام ربات‌ها رد شود" />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            icon={<FilterAltRoundedIcon />}
            title="آستانه‌های دروازه‌بان و نویسنده"
            hint="آستانه = حداقل اطمینان دروازه‌بان برای هر سطح سخت‌گیریِ گروه."
          >
            <Grid container spacing={2}>
              {[
                { l: 'آستانهٔ کم', v: s.gate.thresholdLow, set: (n: number) => nested('gate', { thresholdLow: n }) },
                { l: 'آستانهٔ عادی', v: s.gate.thresholdNormal, set: (n: number) => nested('gate', { thresholdNormal: n }) },
                { l: 'آستانهٔ زیاد', v: s.gate.thresholdHigh, set: (n: number) => nested('gate', { thresholdHigh: n }) },
              ].map((f) => (
                <Grid item xs={4} md={2} key={f.l}>
                  <TextField size="small" type="number" inputProps={{ step: 0.05, min: 0, max: 1 }} label={f.l} value={f.v} onChange={(e) => f.set(Number(e.target.value))} fullWidth />
                </Grid>
              ))}
              <Grid item xs={4} md={2}>
                <TextField size="small" type="number" label="زمینه (دروازه‌بان)" value={s.gate.contextMessages} onChange={(e) => nested('gate', { contextMessages: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField size="small" type="number" label="زمینه (نویسنده)" value={s.writer.contextMessages} onChange={(e) => nested('writer', { contextMessages: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={4} md={2}>
                <TextField size="small" type="number" label="حداکثر طول پاسخ" value={s.writer.maxChars} onChange={(e) => nested('writer', { maxChars: Number(e.target.value) })} fullWidth />
              </Grid>
            </Grid>
          </SectionCard>
        </Stack>
      )}

      {/* ───────── شخصیت ───────── */}
      {group === 'persona' && (
        <Stack spacing={2}>
          <SectionCard
            icon={<PsychologyRoundedIcon />}
            title="قواعد سراسری"
            hint="به همهٔ حساب‌ها اعمال می‌شود؛ قواعد اختصاصی هر حساب در تب حساب‌ها است."
          >
            <TextField value={s.persona.globalRules} onChange={(e) => nested('persona', { globalRules: e.target.value })} fullWidth multiline minRows={7} />
          </SectionCard>
          <SectionCard
            icon={<PsychologyRoundedIcon />}
            title="واقعیت‌های سایت"
            hint="دانش پس‌زمینه — مدل حق ندارد عیناً نقلش کند، فقط بر اساسش حرف می‌زند."
          >
            <TextField value={s.persona.siteFacts} onChange={(e) => nested('persona', { siteFacts: e.target.value })} fullWidth multiline minRows={7} />
          </SectionCard>
          <SectionCard
            icon={<PsychologyRoundedIcon />}
            title="نمونه‌پاسخ‌ها (یادگیری لحن)"
            hint="مؤثرترین راه برای طبیعی‌شدن جواب‌ها: چند جواب واقعی که خودتان در گروه می‌دادید. هر نمونه دو خط: «پیام: …» و «جواب: …». خالی = نمونه‌های پیش‌فرض."
          >
            <TextField value={s.persona.examples || ''} onChange={(e) => nested('persona', { examples: e.target.value })} fullWidth multiline minRows={8} />
          </SectionCard>
        </Stack>
      )}

      {/* ───────── پشتیبان و جابه‌جایی ───────── */}
      {group === 'failover' && (
        <Stack spacing={2}>
          <SectionCard
            icon={<SwapHorizRoundedIcon />}
            title="قاعدهٔ جابه‌جایی"
            hint="وقتی سرویس اصلی اعتبار/سهمیه‌اش تمام شود، دستیار بین سرویس‌ها و مدل‌ها می‌گردد تا یکی که آزاد است پیدا کند."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <SwitchRow
                  checked={s.failover?.enabled !== false}
                  onChange={(v) => nested('failover', { enabled: v })}
                  label="جابه‌جایی خودکار"
                  hint="اگر خاموش باشد، با خرابی سرویس اصلی پیام‌ها فقط در صف می‌مانند."
                />
              </Grid>
              <Grid item xs={12} md={5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>وقتی سرویس خراب شد</InputLabel>
                  <Select
                    label="وقتی سرویس خراب شد"
                    value={s.failover?.mode || (s.failover?.requireApproval === false ? 'auto' : 'ask')}
                    onChange={(e) => nested('failover', { mode: e.target.value as 'auto' | 'ask' | 'off' })}
                  >
                    <MenuItem value="auto">همان لحظه جابه‌جا شو و به من خبر بده (توصیه‌شده)</MenuItem>
                    <MenuItem value="ask">منتظر تأیید من بمان</MenuItem>
                    <MenuItem value="off">هیچ‌وقت جابه‌جا نشو</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  type="number"
                  label="مهلت تأیید (دقیقه)"
                  value={s.failover?.approvalTimeoutMinutes ?? 10}
                  onChange={(e) => nested('failover', { approvalTimeoutMinutes: Number(e.target.value) })}
                  fullWidth
                  disabled={(s.failover?.mode || 'auto') !== 'ask'}
                  helperText="بعد از این مدت خودکار اعمال می‌شود"
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  type="number"
                  label="سنجش سلامت (دقیقه)"
                  value={s.failover?.probeMinutes ?? 5}
                  onChange={(e) => nested('failover', { probeMinutes: Number(e.target.value) })}
                  fullWidth
                  helperText="هر چند دقیقه سرویس اصلی امتحان شود"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  size="small"
                  type="number"
                  label="صبر برای پیام بی‌جواب (ساعت)"
                  value={s.waitMaxHours ?? 24}
                  onChange={(e) => set('waitMaxHours', Number(e.target.value))}
                  fullWidth
                  helperText="۰ = هیچ‌وقت رها نشود"
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { lineHeight: 1.9 } }}>
              پیامی که به‌خاطر خرابی سرویس جواب نگرفته دور ریخته نمی‌شود؛ در مرحلهٔ «منتظر سرویس» می‌ماند و به‌محض درست‌شدن اوضاع خودکار جواب می‌گیرد.
              برگشت به سرویس اصلی همیشه خودکار است و تأیید نمی‌خواهد.
            </Alert>
          </SectionCard>

          <SectionCard
            icon={<NotificationsActiveRoundedIcon />}
            title="خبر بده وقتی خراب شد"
            hint="اعلان پنل فقط وقتی به درد می‌خورد که شما پای پنل باشید. این‌جا شناسهٔ چت تلگرام بگذارید تا خبر مستقیم برسد."
          >
            <TextField
              size="small"
              label="شناسهٔ چت تلگرام"
              value={(s.alertChatIds || []).join(', ')}
              onChange={(e) =>
                set(
                  'alertChatIds',
                  e.target.value
                    .split(/[\s,،]+/)
                    .map((x) => x.trim())
                    .filter(Boolean) as any
                )
              }
              fullWidth
              placeholder="123456789, 987654321"
              helperText="چند تا را با ویرگول جدا کنید. شناسهٔ خودتان را از ربات @userinfobot بگیرید."
              sx={{ '& input': { direction: 'ltr', textAlign: 'left' } }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={alertTesting ? <CircularProgress size={14} /> : <NotificationsActiveRoundedIcon />}
                disabled={alertTesting}
                onClick={async () => {
                  setAlertTesting(true);
                  try {
                    const r = await userbotService.testAlert();
                    enqueueSnackbar(r.message, { variant: r.result.chatIds ? 'success' : 'warning' });
                  } catch (e) {
                    enqueueSnackbar(errMsg(e), { variant: 'error' });
                  } finally {
                    setAlertTesting(false);
                  }
                }}
              >
                یک پیام آزمایشی بفرست
              </Button>
              <Typography variant="caption" color="text.secondary">
                اول ذخیره کنید، بعد آزمایش.
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ mt: 1.5, '& .MuiAlert-message': { lineHeight: 1.9 } }}>
              شرط رسیدن پیام: همان حساب تلگرام باید یک بار ربات کازماشاپ را استارت کرده باشد، وگرنه تلگرام اجازهٔ پیام‌دادن نمی‌دهد.
            </Alert>
          </SectionCard>

          <FailoverCard />
        </Stack>
      )}

      {/* ───────── دایرکت ───────── */}
      {group === 'dm' && (
        <Stack spacing={2}>
          <SectionCard
            icon={<MailRoundedIcon />}
            title="پاسخ به پیام خصوصی"
            hint="جنس دایرکت با گروه فرق دارد: در گروه ما مهمان ناخوانده‌ایم و باید کم و دقیق حرف بزنیم، ولی در دایرکت طرف خودش به ما پیام داده و بی‌جواب گذاشتنش بدترین کار است."
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={5}>
                <SwitchRow
                  checked={s.dm?.enabled ?? false}
                  onChange={(v) => nested('dm', { enabled: v })}
                  label="جواب دادن به دایرکت"
                  hint="تا روشن نشود، هیچ پیام خصوصی‌ای پردازش نمی‌شود."
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>حالت پاسخ</InputLabel>
                  <Select
                    label="حالت پاسخ"
                    value={s.dm?.mode || 'auto'}
                    onChange={(e) => nested('dm', { mode: e.target.value as 'auto' | 'draft' })}
                  >
                    <MenuItem value="auto">ارسال خودکار</MenuItem>
                    <MenuItem value="draft">پیش‌نویس با تأیید من</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl size="small" fullWidth>
                  <InputLabel>تصمیم جواب دادن</InputLabel>
                  <Select
                    label="تصمیم جواب دادن"
                    value={s.dm?.gate || 'always'}
                    onChange={(e) => nested('dm', { gate: e.target.value as 'always' | 'smart' })}
                  >
                    <MenuItem value="always">به همه جواب بده (ارزان‌تر)</MenuItem>
                    <MenuItem value="smart">دروازه‌بان تصمیم بگیرد</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Alert severity="info" sx={{ mt: 2, '& .MuiAlert-message': { lineHeight: 1.9 } }}>
              در حالت «به همه جواب بده» مدل دروازه‌بان اصلاً صدا زده نمی‌شود، پس هزینه کمتر است و هیچ مشتری‌ای بی‌جواب نمی‌ماند.
              هرزنامهٔ آشکار (تبلیغ کانال، لینک خالی، شرط‌بندی) همان‌جا و بدون هوش مصنوعی رد می‌شود.
            </Alert>
          </SectionCard>

          <SectionCard icon={<MailRoundedIcon />} title="محدودیت‌ها" hint="در دایرکت فاصلهٔ گروهی معنا ندارد؛ فقط همین نفر و سقف روزانه.">
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField
                  size="small"
                  type="number"
                  label="فاصله با همان نفر (دقیقه)"
                  value={s.dm?.perUserMinutes ?? 2}
                  onChange={(e) => nested('dm', { perUserMinutes: Number(e.target.value) })}
                  fullWidth
                  helperText="۰ = بی‌محدودیت"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  size="small"
                  type="number"
                  label="سقف پاسخ خصوصی در روز"
                  value={s.dm?.maxPerDay ?? 120}
                  onChange={(e) => nested('dm', { maxPerDay: Number(e.target.value) })}
                  fullWidth
                  helperText="۰ = بی‌محدودیت"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField
                  size="small"
                  type="number"
                  inputProps={{ step: 0.05, min: 0, max: 1 }}
                  label="آستانهٔ دروازه‌بان"
                  value={s.dm?.gateThreshold ?? 0.35}
                  onChange={(e) => nested('dm', { gateThreshold: Number(e.target.value) })}
                  fullWidth
                  disabled={(s.dm?.gate || 'always') === 'always'}
                  helperText="فقط در حالت دروازه‌بان"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <SwitchRow
                  checked={s.dm?.ignoreForwarded ?? true}
                  onChange={(v) => nested('dm', { ignoreForwarded: v })}
                  label="فوروارد رد شود"
                  hint="پیام فوروارد‌شده در پیوی معمولاً تبلیغ است."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SwitchRow
                  checked={s.dm?.respectQuietHours ?? false}
                  onChange={(v) => nested('dm', { respectQuietHours: v })}
                  label="ساعت سکوت برای دایرکت هم اعمال شود"
                  hint="پیش‌فرض خاموش است: کسی که نصف شب پیام می‌دهد هم جواب می‌خواهد."
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SwitchRow
                  checked={s.dm?.mayMentionSiteFirst ?? true}
                  onChange={(v) => nested('dm', { mayMentionSiteFirst: v })}
                  label="از همان جواب اول می‌تواند کازماشاپ را بگوید"
                  hint="در گروه معرفی مرحله‌ای است، ولی این‌جا طرف خودش سراغ ما آمده."
                />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard
            icon={<PsychologyRoundedIcon />}
            title="رفتار در دایرکت"
            hint="این متن مستقیم به مدل داده می‌شود و جای «یادداشت گروه» را می‌گیرد."
          >
            <TextField
              fullWidth
              multiline
              minRows={8}
              size="small"
              value={s.dm?.notes || ''}
              onChange={(e) => nested('dm', { notes: e.target.value })}
            />
          </SectionCard>
        </Stack>
      )}

      {/* ───────── رفتار ───────── */}
      {group === 'behavior' && (
        <Stack spacing={2}>
          <SectionCard icon={<ScheduleRoundedIcon />} title="رفتار انسانی" hint="تأخیر و تایپینگ قبل از ارسال، تا پیام‌ها ربات‌گونه نباشند.">
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <TextField size="small" type="number" label="حداقل تأخیر (ثانیه)" value={s.humanize.minDelaySec} onChange={(e) => nested('humanize', { minDelaySec: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" type="number" label="حداکثر تأخیر (ثانیه)" value={s.humanize.maxDelaySec} onChange={(e) => nested('humanize', { maxDelaySec: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" type="number" label="تایپ هر کاراکتر (ms)" value={s.humanize.typingMsPerChar} onChange={(e) => nested('humanize', { typingMsPerChar: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={6} md={3}>
                <TextField size="small" type="number" label="انقضای پیش‌نویس (دقیقه)" value={s.draftExpiryMinutes} onChange={(e) => set('draftExpiryMinutes', Number(e.target.value))} fullWidth />
              </Grid>
            </Grid>
          </SectionCard>

          <SectionCard icon={<ScheduleRoundedIcon />} title="هماهنگی حساب‌ها و ساعت سکوت">
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField size="small" type="number" label="پیوستگی گفتگو (دقیقه)" value={s.coordination.handoffMinutes} onChange={(e) => nested('coordination', { handoffMinutes: Number(e.target.value) })} fullWidth helperText="تا این مدت، همان حساب با همان نفر ادامه می‌دهد" />
              </Grid>
              <Grid item xs={12} md={5}>
                <SwitchRow
                  checked={s.coordination.backupAllowed}
                  onChange={(v) => nested('coordination', { backupAllowed: v })}
                  label="جایگزینی حساب"
                  hint="اگر حساب صاحب گفتگو آفلاین بود، حساب دیگری ادامه دهد."
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <SwitchRow
                  checked={s.quietHours.enabled}
                  onChange={(v) => nested('quietHours', { enabled: v })}
                  label="ساعت سکوت"
                  hint="به وقت تهران؛ در این بازه هیچ پیامی پردازش نمی‌شود."
                />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField size="small" type="number" label="از ساعت" value={s.quietHours.from} onChange={(e) => nested('quietHours', { from: Number(e.target.value) })} fullWidth />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField size="small" type="number" label="تا ساعت" value={s.quietHours.to} onChange={(e) => nested('quietHours', { to: Number(e.target.value) })} fullWidth />
              </Grid>
            </Grid>
          </SectionCard>
        </Stack>
      )}

      {/* ───────── نوار ذخیره ───────── */}
      <div className={`ubc-savebar ${dirty ? 'dirty' : ''}`}>
        <b style={{ fontSize: 12.5, color: dirty ? 'var(--color-warning-dark)' : 'var(--color-text-secondary)' }}>
          {dirty ? 'تغییرات ذخیره‌نشده دارید' : 'همه‌چیز ذخیره است'}
        </b>
        <span style={{ flex: 1 }} />
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          disabled={saving || !dirty}
          onClick={save}
        >
          ذخیرهٔ تنظیمات
        </Button>
      </div>
    </Box>
  );
};

export default SettingsTab;
